





















































import Vue from 'vue';
import { timeDay } from 'd3-time';
import { IOfferOutcomeCount, IState } from '@/store/types';
import { TopLevelSpec } from 'vega-lite';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { LOAD_STATE } from '@/store/shared';
import { genOfferOutcomeBar } from '@/components/Vis/Vega/Specs/OfferOutcomeBar';
import { mapGetters, mapState } from 'vuex';
import * as actions from '@/store/action-types';
import { formatPercent } from '@/helpers/jsUtils';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import HorizontalBarLoader from '@/components/UI/Loader/HorizontalBarLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';

export default Vue.extend({
  name: 'OfferOutcomes',
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -90);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      startDate,
      endDate,
      offerStack: false,
      initialLoading: true,
      filter: {} as Record<string, string[]>,
    };
  },
  components: {
    VueVegaEmbed,
    DatePickerCondensed,
    SessionFilter,
    HeaderLoader,
    HorizontalBarLoader,
  },
  async created() {
    await this.$store.dispatch(actions.FETCH_OFFER_OUTCOMES, { filter: { startDate: this.startDate, endDate: this.endDate } });
    if (this.offerOutcomes.reduce((sum, d) => sum + d.accepted + d.rejected, 0) < 30 && this.daysSinceOrgCreated && this.daysSinceOrgCreated > 90) {
      await this.$store.dispatch(actions.FETCH_OFFER_OUTCOMES, { filter: { startDate: this.orgCreatedDate, endDate: this.endDate } });
      this.startDate = this.orgCreatedDate;
    }
    this.initialLoading = false;
  },
  computed: {
    ...mapState(['offerOutcomesLoadState']),
    ...mapGetters(['offerOutcomeToString', 'daysSinceOrgCreated', 'orgCreatedDate', 'waitForData']),
    offerOutcomes(): IOfferOutcomeCount[] {
      return this.$store.state.offerOutcomes;
    },
    loading(): boolean {
      const state = this.$store.state as IState;
      return state.offerOutcomesLoadState !== LOAD_STATE.LOADED || state.stripeData.couponsLoadState !== LOAD_STATE.LOADED;
    },
    offerAcceptanceChart(): TopLevelSpec | null {
      if (!this.loading && this.offerOutcomes.length) {
        const chartData = this.offerOutcomes.reduce((acc, item) => {
          acc.push({
            Offer: this.offerOutcomeToString(item),
            Outcome: 'Accepted',
            Count: item.accepted,
            'Acceptance Rate': formatPercent(item.acceptanceRate),
          });
          acc.push({
            Offer: this.offerOutcomeToString(item),
            Outcome: 'Declined',
            Count: item.rejected,
            'Decline Rate': formatPercent(1 - item.acceptanceRate),
          });
          return acc;
        }, [] as any[]);
        return genOfferOutcomeBar({
          data: chartData,
          xField: 'Offer',
          yField: 'Count',
          colorField: 'Outcome',
          xLabels: true,
          yLabels: true,
          height: 240,
          colorScale: {
            domain: ['Accepted', 'Declined'],
            range: ['#3c5ccf', '#D8DFF6'],
          },
        });
      }
      return null;
    },
  },
  methods: {
    fetchData() {
      const mappedFilter = Object.keys(this.filter).reduce(
        (acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        },
        {} as Record<string, any>
      );
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      this.$store.dispatch(actions.FETCH_OFFER_OUTCOMES, { filter: mappedFilter });
    },
    handleDateChange(value: [Date, Date]): void {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchData();
    },
    onFilterUpdate(newFilter: Record<string, string[]>) {
      this.filter = newFilter;
      this.fetchData();
    },
    exportOfferOutcomes() {
      // annoying format
      if (this.offerAcceptanceChart) {
        this.$store.dispatch(actions.EXPORT_DATA, {
          // @ts-ignore
          data: this.offerAcceptanceChart.data.values,
          name: 'offer-performance',
          fields: ['Offer', 'Outcome', 'Count', 'Acceptance Rate', 'Decline Rate'],
          csv: true,
        });
      }
    },
  },
});
